import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Check, MapPin } from "lucide-react";
import { RELOCATION_GUIDE_PDF } from "../data";
import { Reveal } from "./Reveal";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Input = "w-full bg-white text-[#0a0a0a] border border-black/20 px-4 py-3 text-base focus:outline-none focus:ring-1 focus:ring-[#0a0a0a] focus:border-[#0a0a0a] transition-colors";

const GUIDE_POINTS = [
  "A plain-English comparison of the top IE cities",
  "What your money actually buys vs. the coast",
  "How to research schools the right way",
  "Commute realities & a relocation checklist"
];

const triggerDownload = () => {
  const a = document.createElement("a");
  a.href = RELOCATION_GUIDE_PDF;
  a.download = "Moving-to-the-Inland-Empire-Guide.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const LeadMagnet = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        source: "guide_download",
        name: name || null,
        email,
        message: "Downloaded the free 'Moving to the Inland Empire' relocation guide"
      });
      trackLead({ source: "guide_download" });
      setDone(true);
      triggerDownload();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="relocation-guide" data-testid="lead-magnet-section" className="py-16 md:py-24 bg-[#0a0a0a] text-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="text-gold" size={22} />
            <p className="eyebrow text-gold">Free Relocation Guide</p>
          </div>
          <h2 className="font-heading font-light text-3xl sm:text-5xl tracking-tight leading-tight">
            Moving to the Inland Empire
          </h2>
          <p className="text-white/70 font-light text-lg mt-5 max-w-xl leading-relaxed">
            Priced out of LA or Orange County? Get the honest, local buyer's guide to relocating —
            city-by-city comparisons, school tips, commute realities, and a step-by-step checklist.
          </p>
          <ul className="mt-7 space-y-3">
            {GUIDE_POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-white/80 font-light text-sm">
                <MapPin size={16} className="text-gold shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </Reveal>

        <div className="lg:col-span-5">
          {done ? (
            <Reveal>
              <div className="bg-white text-[#0a0a0a] p-8 sm:p-10 text-center" data-testid="lead-magnet-success">
                <div className="w-14 h-14 bg-gold/10 flex items-center justify-center mx-auto mb-5">
                  <Check className="text-gold" size={26} />
                </div>
                <h4 className="font-heading text-2xl">Your guide is on the way!</h4>
                <p className="text-[#52525b] font-light mt-3">
                  The download should have started automatically. If not, tap the button below.
                </p>
                <button
                  onClick={triggerDownload}
                  data-testid="lead-magnet-redownload"
                  className="inline-flex items-center gap-2 mt-6 bg-[#0a0a0a] text-white text-sm px-7 py-3.5 hover:bg-[#27272a] transition-colors"
                >
                  <Download size={15} /> Download the guide
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="bg-white/5 border border-white/10 p-8 sm:p-10 space-y-4" data-testid="lead-magnet-form">
                <h4 className="font-heading text-2xl text-white">Get your free copy</h4>
                <p className="text-white/60 font-light text-sm">Enter your details and we'll send it over instantly.</p>
                <input data-testid="lead-magnet-name" className={Input} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
                <input data-testid="lead-magnet-email" type="email" className={Input} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                <button data-testid="lead-magnet-submit" type="submit" disabled={loading} className="flex items-center justify-center gap-2 w-full bg-gold text-white py-4 text-sm tracking-wide hover:bg-gold-hover transition-colors disabled:opacity-60">
                  {loading ? "Sending..." : "Download Free Guide"} <Download size={16} />
                </button>
                <p className="text-white/40 font-light text-xs text-center">No spam — just helpful local insight.</p>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
